<template>
  <section class="panel task">
    <!-- Header / File info -->
    <div class="panel-hd">
      <div v-if="task">
        <div class="panel-title">{{ task.fileName }}</div>
        <div class="panel-sub">
          {{ formatBytes(task.totalBytes) }}
          <span v-if="task.fileId" class="muted"> · fileId: {{ task.fileId.slice(0, 8) }}...</span>
        </div>
      </div>

      <div v-else>
        <div class="panel-title muted">No file selected</div>
        <div class="panel-sub">Choose a file to create an upload task.</div>
      </div>

      <span class="status-text" :class="{ off: !task }">
        {{ task ? statusText(task.status) : "IDLE" }}
      </span>
    </div>

    <!-- Progress area (reserved) -->
    <div class="progress">
      <div class="bar">
        <div class="fill" :style="{ width: `${task ? task.percent : 0}%` }"></div>
      </div>

      <div class="progress-gap"></div>

      <div class="meta">
        <template v-if="task">
          <span>{{ task.percent.toFixed(1) }}%</span>
          <span class="dot">·</span>
          <span>{{ formatBytes(task.uploadedBytes) }} / {{ formatBytes(task.totalBytes) }}</span>
          <template v-if="task.speedBps">
            <span class="dot">·</span>
            <span>{{ formatBytes(task.speedBps) }}/s</span>
          </template>
          <template v-if="task.etaSec">
            <span class="dot">·</span>
            <span>ETA {{ formatDuration(task.etaSec) }}</span>
          </template>
        </template>

        <template v-else>
          <span class="muted">0%</span>
          <span class="dot">·</span>
          <span class="muted">0 B / 0 B</span>
        </template>
      </div>
    </div>

    <!-- Controls -->
    <div class="panel-ft">
      <button class="btn2 primary" @click="pickFile" :disabled="!ready">Choose File</button>
      <input ref="fileInput" class="hidden" type="file" @change="onFileChange" />

      <button class="btn2 primary" :disabled="!task || task.status !== 'selected' || task._busy" @click="startTask">
        {{ task && task._busy && task.status === "selected" ? "Working..." : "Start" }}
      </button>

      <button class="btn2" :disabled="!task || task.status !== 'uploading' || task._busy" @click="pauseTask">
        Pause
      </button>

      <button class="btn2 primary" :disabled="!task || !canResume(task) || task._busy" @click="resumeTask">
        {{ task && task._busy && (task.status === "paused" || task.status === "error") ? "Working..." : "Resume" }}
      </button>

      <button class="btn2 danger" :disabled="!task || task.status === 'uploading' || task._busy" @click="removeTask">
        Remove
      </button>
    </div>

    <!-- Messages -->
    <div v-if="task && task.errorMsg" class="err">
      {{ task.errorMsg }}
    </div>
  </section>
</template>

<script>
import axios from "axios"
import * as tus from "tus-js-client"
import { v4 as uuidv4 } from "uuid"

export default {
  name: "UploadPanel",

  props: {
    /** e.g. API_BASE_URL or http://xxx */
    apiBaseUrl: { type: String, required: true },

    /** default: /api/common/upload_init */
    initPath: { type: String, default: "/api/common/upload_init" },

    /** default: /api/common/upload_create */
    createPath: { type: String, default: "/api/common/upload_create" },

    /** default: /api/common/upload_complete */
    completePath: { type: String, default: "/api/common/upload_complete" },
  },

  data() {
    return {
      cfg: { tusEndpoint: "", maxSizeBytes: null },
      task: null,
      pollTimer: null,
      saveTimer: null,
    }
  },

  computed: {
    ready() {
      return !!this.cfg.tusEndpoint
    },
  },

  mounted() {
    window.addEventListener("offline", this.onOffline)
    ;(async () => {
      await this.fetchInit()
    })()
  },

  beforeUnmount() {
    window.removeEventListener("offline", this.onOffline)
    this.stopPoll()
    if (this.saveTimer) clearTimeout(this.saveTimer)
  },

  methods: {
    statusText(s) {
      return (
        {
          selected: "READY",
          uploading: "UPLOADING",
          paused: "PAUSED",
          done: "DONE",
          error: "ERROR",
        }[s] || String(s || "")
      )
    },

    canResume(t) {
      return (t.status === "paused" || t.status === "error") && !!t.tusUploadUrl
    },

    formatBytes(n) {
      if (!n) return "0 B"
      const u = ["B", "KB", "MB", "GB", "TB"]
      let i = 0
      while (n >= 1024 && i < u.length - 1) {
        n /= 1024
        i++
      }
      return `${n.toFixed(n >= 10 ? 0 : 1)} ${u[i]}`
    },

    formatDuration(sec) {
      if (!sec || !isFinite(sec)) return ""
      const m = Math.floor(sec / 60)
      const s = Math.floor(sec % 60)
      return m ? `${m}m ${s}s` : `${s}s`
    },

    sleep(ms) {
      return new Promise((r) => setTimeout(r, ms))
    },

    // PATCH: init 异常处理（给出可见错误，且不崩组件）
    async fetchInit() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}${this.initPath}`)
        this.cfg.tusEndpoint = res.data.tusEndpoint
        this.cfg.maxSizeBytes = res.data.maxSizeBytes

        if (!this.cfg.tusEndpoint) {
          // 保底：即使接口成功也可能没字段
          this.cfg.tusEndpoint = ""
          this.task = this.task || null
          // 不强行创建 task，只提示
          console.warn("[UploadPanel] tusEndpoint missing from init response")
        }
      } catch (e) {
        console.error("[UploadPanel] fetchInit failed:", e)
        this.cfg.tusEndpoint = ""
        this.cfg.maxSizeBytes = null

        // 在没有 task 的情况下也给用户提示
        if (!this.task) {
          this.task = {
            id: uuidv4(),
            file: null,
            fileId: "",
            fileName: "",
            fileType: "",
            totalBytes: 0,
            tusUploadUrl: "",
            status: "error",
            percent: 0,
            uploadedBytes: 0,
            speedBps: 0,
            etaSec: 0,
            _lastTs: 0,
            _lastUploaded: 0,
            upload: null,
            errorMsg: "Init failed: cannot load upload config. Please refresh or check network.",
            _busy: false,
            _starting: false,
            _aborting: false,
            // PATCH: 轮询失败计数（即使这里不会轮询，也保持字段一致）
            _headFailCount: 0,
          }
        } else {
          this.task.status = "error"
          this.task.errorMsg = "Init failed: cannot load upload config. Please refresh or check network."
        }
      }
    },

    async createRecord(file) {
      const r = await axios.post(`${this.apiBaseUrl}${this.createPath}`, {
        filename: file.name,
        size: file.size,
        type: file.type,
      })
      return r.data.fileId
    },

    async completeRecord(t) {
      await axios.post(`${this.apiBaseUrl}${this.completePath}`, {
        fileId: t.fileId,
        tusUploadUrl: t.tusUploadUrl,
        filename: t.fileName,
        size: t.totalBytes,
        type: t.fileType || "",
      })
    },

    saveTaskDebounced() {
      if (this.saveTimer) return
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null
        // no persistence (kept only to avoid doing heavy work too frequently if you add any later)
      }, 800)
    },

    pickFile() {
      this.$refs.fileInput?.click()
    },

    async onFileChange(e) {
      const f = e.target.files?.[0]
      e.target.value = ""
      if (!f) return

      // PATCH: maxSize 判空（maxSizeBytes 为 null/undefined 时不做限制）
      if (typeof this.cfg.maxSizeBytes === "number" && isFinite(this.cfg.maxSizeBytes) && this.cfg.maxSizeBytes > 0) {
        if (f.size > this.cfg.maxSizeBytes) {
          alert(`File too large: ${f.name} (max ${this.formatBytes(this.cfg.maxSizeBytes)})`)
          return
        }
      }

      if (this.task) {
        await this.removeTask({ silent: true })
      }

      this.task = {
        id: uuidv4(),
        file: f,
        fileId: "",
        fileName: f.name,
        fileType: f.type,
        totalBytes: f.size,
        tusUploadUrl: "",
        status: "selected",
        percent: 0,
        uploadedBytes: 0,
        speedBps: 0,
        etaSec: 0,
        _lastTs: 0,
        _lastUploaded: 0,
        upload: null,
        errorMsg: "",
        _busy: false,
        _starting: false,
        _aborting: false,

        // PATCH: HEAD 轮询失败计数
        _headFailCount: 0,
      }
    },

    /* HEAD polling: paused/error only; never while uploading */
    async headProgress() {
      const t = this.task
      if (!t?.tusUploadUrl) return
      if (t.status === "uploading") return

      try {
        const r = await fetch(t.tusUploadUrl, {
          method: "HEAD",
          headers: { "Tus-Resumable": "1.0.0" },
        })

        // PATCH: 成功拿到响应就清零失败计数（即使是 404/5xx 也算“有响应”，但下面会处理）
        t._headFailCount = 0

        if (r.status === 404) {
          t.status = "error"
          t.errorMsg = "Upload resource not found on server. Please re-upload."
          this.stopPoll()
          return
        }

        if (r.status >= 500) {
          t.errorMsg = t.errorMsg || "Server error. Will retry..."
          return
        }

        const offset = Number(r.headers.get("Upload-Offset"))
        const length = Number(r.headers.get("Upload-Length"))
        if (!isFinite(offset) || !isFinite(length) || length <= 0) return

        t.uploadedBytes = offset
        t.totalBytes = length
        t.percent = (offset / length) * 100
      } catch {
        // PATCH: 轮询失败计数 + 阈值提示
        t._headFailCount = (t._headFailCount || 0) + 1
        if (t._headFailCount >= 5) {
          t.status = "error"
          t.errorMsg = "Cannot query upload progress. Please check network or resume again."
          this.stopPoll()
        }
      }
    },

    startPoll() {
      this.stopPoll()
      const t = this.task
      if (!t?.tusUploadUrl) return
      if (t.status === "uploading") return

      // PATCH: 开始轮询前重置计数
      t._headFailCount = 0

      this.headProgress()
      this.pollTimer = setInterval(() => this.headProgress(), 3000)
    },

    stopPoll() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
    },

    ensureUploadInstance(t) {
      if (t.upload) return t.upload
      if (!t.file) throw new Error("Missing File object. Please choose the file again.")

      const upload = new tus.Upload(t.file, {
        endpoint: this.cfg.tusEndpoint,
        chunkSize: 2 * 1024 * 1024,
        retryDelays: [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000],

        storeFingerprintForResuming: true,
        fingerprint: () => Promise.resolve(`tus-fileId-${t.fileId}`),

        metadata: {
          fileId: t.fileId,
          filename: t.fileName,
          type: t.fileType || "",
        },

        onProgress: (bytesUploaded, bytesTotal) => {
          t.uploadedBytes = bytesUploaded
          t.totalBytes = bytesTotal
          t.percent = bytesTotal ? (bytesUploaded / bytesTotal) * 100 : 0

          const now = Date.now()
          const dt = (now - t._lastTs) / 1000
          if (dt > 0 && t._lastTs) {
            const v = (bytesUploaded - t._lastUploaded) / dt
            if (v > 0) {
              t.speedBps = v
              t.etaSec = (bytesTotal - bytesUploaded) / v
            } else {
              t.speedBps = 0
              t.etaSec = 0
            }
          }
          t._lastTs = now
          t._lastUploaded = bytesUploaded

          this.saveTaskDebounced()
        },

        onAfterResponse: () => {
          if (upload.url && !t.tusUploadUrl) {
            t.tusUploadUrl = upload.url
          }
        },

        onError: (err) => {
          t.status = "error"
          t.errorMsg = err?.message || "Upload failed"
          t.speedBps = 0
          t.etaSec = 0
          this.startPoll()
        },

        onSuccess: async () => {
          t.percent = 100
          t.status = "done"
          t.errorMsg = ""
          t.speedBps = 0
          t.etaSec = 0

          try {
            await this.completeRecord(t)
          } catch {
            t.status = "error"
            t.errorMsg = "Upload finished, but backend finalize failed."
            return
          }

          this.stopPoll()

          // The ONLY public event
          this.$emit("done", {
            fileId: t.fileId,
            filename: t.fileName,
            size: t.totalBytes,
            type: t.fileType || "",
            tusUploadUrl: t.tusUploadUrl,
          })
        },
      })

      t.upload = upload
      return upload
    },

    async withTaskLock(fn) {
      const t = this.task
      if (!t || t._busy) return
      t._busy = true
      try {
        await fn(t)
      } finally {
        t._busy = false
      }
    },

    async startTask() {
      await this.withTaskLock(async (t) => {
        if (t.status !== "selected") return
        if (t._starting) return

        // PATCH: _starting 用 try/finally 兜底，避免异常卡死
        t._starting = true
        try {
          t.errorMsg = ""
          t.status = "uploading"
          t.speedBps = 0
          t.etaSec = 0
          this.stopPoll()

          if (!t.fileId) {
            t.fileId = await this.createRecord(t.file)
          }

          const upload = this.ensureUploadInstance(t)
          const prev = await upload.findPreviousUploads()
          if (prev.length) upload.resumeFromPreviousUpload(prev[0])

          upload.start()
        } catch (e) {
          t.status = "error"
          t.errorMsg = e?.message || "Failed to start upload"
          t.speedBps = 0
          t.etaSec = 0
          this.startPoll()
        } finally {
          t._starting = false
        }
      })
    },

    async pauseTask({ silent = false } = {}) {
      await this.withTaskLock(async (t) => {
        if (t.status !== "uploading") return

        if (!silent) t.errorMsg = ""
        t.status = "paused"
        t.speedBps = 0
        t.etaSec = 0

        // PATCH: abort 尽量 await（不同版本 tus-js-client 兼容）
        if (t.upload && !t._aborting) {
          t._aborting = true
          try {
            const ret = t.upload.abort()
            if (ret && typeof ret.then === "function") {
              await ret
            }
          } catch {
            // ignore
          } finally {
            t._aborting = false
          }
        }

        this.startPoll()
      })
    },

    async resumeTask() {
      await this.withTaskLock(async (t) => {
        if (!this.canResume(t)) return
        if (t._starting) return

        // PATCH: _starting try/finally 兜底
        t._starting = true
        try {
          t.errorMsg = ""
          this.stopPoll()

          if (!t.file) {
            t.status = "error"
            t.errorMsg = "Please choose the same file again to resume."
            return
          }

          if (!t.fileId) {
            t.fileId = await this.createRecord(t.file)
          }

          t.status = "uploading"

          const upload = this.ensureUploadInstance(t)
          const prev = await upload.findPreviousUploads()
          if (prev.length) upload.resumeFromPreviousUpload(prev[0])

          upload.start()
        } catch (e) {
          t.status = "error"
          t.errorMsg = e?.message || "Failed to resume upload"
          t.speedBps = 0
          t.etaSec = 0
          this.startPoll()
        } finally {
          t._starting = false
        }
      })
    },

    async removeTask({ silent = false } = {}) {
      const t = this.task
      if (!t) return

      await this.withTaskLock(async (tt) => {
        this.stopPoll()
        if (tt.upload) {
          // PATCH: remove 时也尽量 await abort，避免残留请求
          try {
            const ret = tt.upload.abort()
            if (ret && typeof ret.then === "function") await ret
          } catch {
            // ignore
          }
          await this.sleep(100)
        }
        this.task = null
        if (!silent) {
          // no persistence
        }
      })
    },

    async onOffline() {
      const t = this.task
      if (!t) return
      if (t.status === "uploading") {
        t.errorMsg = "Network offline. Auto-paused."
        await this.pauseTask({ silent: true })
      }
    },
  },
}
</script>

<style scoped>
.hidden { display:none; }

.panel {
  margin-top:14px;
  background:#fff;
  border:1px solid #eee;
  border-radius:14px;
  padding:14px;
}

.panel-hd { display:flex; justify-content:space-between; align-items:center; gap:10px; }
.panel-title { font-weight:800; }
.panel-sub { font-size:12px; color:#666; }

.panel-ft { display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap; margin-top:14px; }

.btn2 { border:1px solid #ddd; border-radius:10px; padding:8px 12px; background:#fff; cursor:pointer; }
.btn2.primary { background:#111; color:#fff; border-color:#111; }
.btn2.danger { color:#b00020; border-color:#f1c3c6; }
.btn2:disabled { opacity:.45; cursor:not-allowed; }

.progress { margin-top:16px; }
.progress .bar {
  height:18px;
  background:#eee;
  border-radius:999px;
  overflow:hidden;
}
.progress .fill {
  height:100%;
  background:#111;
  transition:width .2s ease;
}
.progress-gap { height:12px; }
.progress .meta { display:flex; gap:10px; font-size:13px; color:#555; flex-wrap:wrap; }
.dot { color:#aaa; }

.status-text {
  font-size:12px;
  color:#555;
  background:#f7f7f7;
  border-radius:999px;
  padding:4px 10px;
  white-space:nowrap;
}
.status-text.off { color:#888; }

.task .err {
  margin-top:10px;
  font-size:12px;
  color:#b00020;
  background:#fdecec;
  border-radius:10px;
  padding:8px 10px;
  line-height:1.35;
}
.muted { color:#999; }
</style>


